import { useEffect, useRef, useState } from "react";
import { Alert, Popover, Progress, Table, Tag } from "antd";
import { DownOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import { normalizeReAssessmentFilter } from "./re-assessment-filter-query";
import "../quick-filter/quick-filter-bar.css";
import "./re-assessment-stats.css";

// Sunucudaki doktor satırlarının toplamı. Oran sunucuyla aynı formül (değişen / kapanmış, 1 ondalık):
// genel oran doktor oranlarının ortalaması DEĞİL, aksi halde az kayıtlı doktor sonucu çarpıtır.
// Toplam, sınıflandırılamayan eski kayıtları da içerdiği için değişen + aynı + açık'tan büyük olabilir.
const summarize = (rows) => {
    const sum = (key) => rows.reduce((acc, row) => acc + (row[key] || 0), 0);
    const changed = sum('changed');
    const unchanged = sum('unchanged');
    const closed = changed + unchanged;

    return {
        total: sum('total'),
        changed,
        unchanged,
        open: sum('open'),
        changeRate: closed === 0 ? null : Math.round(changed * 1000 / closed) / 10,
    };
};

function StatCard({ label, value, tone }) {
    return (
        <div className="qf-group ra-stat">
            <span className="ra-stat-label">{label}</span>
            <strong className={`ra-stat-value${tone ? ` is-${tone}` : ''}`}>{value}</strong>
        </div>
    );
}

/**
 * Grid'in üstündeki istatistik şeridi: uygulanan filtrenin özeti + ilk okuyan doktora göre kırılım (açılır).
 *
 * Grid'le aynı filtreye (queryModel.filter) bakar — filtre sol panelden de hızlı filtre çubuğundan da
 * uygulanabildiği için tek doğru kaynak orası. Sayfa/sıralama filtre nesnesini yenilemediğinden yalnız
 * filtre değişince yeniden sorgulanır.
 *
 * Sayının anlamı konusunda kırılımda açık uyarı var: sonucu ("değişti / aynı kaldı") raporu ONAYLAYAN
 * kullanıcı seçiyor ve bu çoğu kurulumda doktorun kendisi değil. Tablo, kapatan kullanıcının
 * beyanının ilk okuyan doktora atfedilmiş hâlidir — doktorun kendi beyanı değildir.
 */
export default function ReAssessmentStats() {
    const { t } = useTranslation();
    const { queryModel } = useDataGrid();

    const filter = queryModel?.filter;
    const ready = !!filter && Object.keys(filter).length > 0;

    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(false);
    // Çubukta art arda tıklanınca yanıtlar sırasız dönebilir; yalnız son isteğin sonucu gösterilsin.
    const lastRequest = useRef(0);

    const load = async () => {
        const request = ++lastRequest.current;

        setLoading(true);
        try {
            const response = await ApiService.ReAssessment.GetStatistics(normalizeReAssessmentFilter(filter));
            if (request === lastRequest.current && response.success) {
                setRows(response.data || []);
            }
        } finally {
            if (request === lastRequest.current) {
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        if (ready) {
            load();
        }
    }, [ready ? JSON.stringify(filter) : null]);

    if (!ready)
        return null;

    const summary = rows ? summarize(rows) : null;
    const show = (value) => (summary ? value : '–');
    // Sıfır renklenmesin: renk "burada bir şey var" demek (eski tablodaki etiketlerle aynı kural).
    const toneOf = (value, tone) => (value > 0 ? tone : undefined);

    const columns = [
        {
            title: t('Original Read User'),
            dataIndex: 'originalReadUserName',
            key: 'originalReadUserName',
            render: (text) => text || t('Unspecified'),
        },
        { title: t('Total'), dataIndex: 'total', key: 'total', width: 90 },
        {
            title: t('Report Changed'),
            dataIndex: 'changed',
            key: 'changed',
            width: 130,
            render: (value) => <Tag color={value > 0 ? 'red' : 'default'}>{value}</Tag>,
        },
        {
            title: t('Report Unchanged'),
            dataIndex: 'unchanged',
            key: 'unchanged',
            width: 140,
            render: (value) => <Tag color={value > 0 ? 'green' : 'default'}>{value}</Tag>,
        },
        {
            title: t('Open'),
            dataIndex: 'open',
            key: 'open',
            width: 90,
            render: (value) => <Tag color={value > 0 ? 'orange' : 'default'}>{value}</Tag>,
        },
        {
            title: t('Change Rate'),
            dataIndex: 'changeRate',
            key: 'changeRate',
            width: 180,
            render: (value) => (value === null || value === undefined)
                ? '-'
                : <Progress percent={Number(value)} size={'small'} status={'normal'} />,
        },
    ];

    return (
        <div className={`ra-stats${loading ? ' is-loading' : ''}`} aria-busy={loading} aria-label={t('Statistics')}>
            <StatCard label={t('Total')} value={show(summary?.total)} />
            <StatCard label={t('Report Changed')} value={show(summary?.changed)} tone={toneOf(summary?.changed, 'changed')} />
            <StatCard label={t('Report Unchanged')} value={show(summary?.unchanged)} tone={toneOf(summary?.unchanged, 'unchanged')} />
            <StatCard label={t('Open')} value={show(summary?.open)} tone={toneOf(summary?.open, 'open')} />
            <StatCard
                label={t('Change Rate')}
                value={show(summary?.changeRate === null ? '-' : `${summary?.changeRate}%`)}
            />

            <div className="qf-group">
                <Popover
                    trigger="click"
                    placement="bottomLeft"
                    content={(
                        <div className="ra-breakdown">
                            <Alert
                                type="info"
                                showIcon
                                message={t('The result is selected by the user who confirms the report, which is often not the doctor. These numbers are attributed to the doctor who read the first version.')}
                            />
                            <Table
                                rowKey={(record) => record.originalReadUserId || 'unspecified'}
                                size={'small'}
                                loading={loading}
                                columns={columns}
                                dataSource={rows || []}
                                pagination={false}
                            />
                        </div>
                    )}
                >
                    <button type="button" className="qf-chip qf-chip-more">
                        <TeamOutlined aria-hidden="true" />
                        {t('Doctors')}
                        {rows && <span className="ra-doctor-count">{rows.length}</span>}
                        <DownOutlined aria-hidden="true" />
                    </button>
                </Popover>
            </div>
        </div>
    );
}
